import React, { ReactElement } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, ScrollParams } from 'react-virtualized';

import _ from 'lodash';

import { AppSettings } from '@/app/AppSettings';
import { Log } from '@/parsing/Log';
import { LogEntryType } from '@/parsing/LogEntry';
import { NetworkRequestOutcome } from '@/parsing/parsers/common/NetworkRequestOutcome';

import { ActiveHighlightableString } from '../highlighting/ActiveHighlightableString';
import { LogViewerPostProcessing } from '../LogViewerPostProcessing';
import { Map } from '../map/Map';
import {
  isNetworkOutcomeFilterActive,
  NetworkOutcomeFilter,
  NetworkOutcomeFilterSide,
} from '../NetworkOutcomeFilter';
import { FoundOccurancesPerHighlighter } from '../post-processing/PostProcessor';
import { Timeline, TimelineDelegate } from '../timeline/Timeline';
import { MapEventInfo, TimelineData } from '../timeline/TimelineData';
import { TimelineDataGenerator } from '../timeline/TimelineDataGenerator';
import { LogViewerTableHeader } from './LogViewerTableHeader';
import { LogViewerTableRowModel, LogViewerTableRowType } from './LogViewerTableRowModel';
import {
  LogViewerTableModel,
  LogViewerTableModelDelegate,
  makeLogViewerTableModel,
  ModelChangeContext,
} from './model/LogViewerTableModel';
import { FileDividerRow } from './rows/file-divider/FileDividerRow';
import { FileDividerRowModel } from './rows/file-divider/FileDividerRowModel';
import { LogEntryNetworkCellModel } from './rows/log-entry/cells/LogEntryNetworkCellModel';
import { LogEntryRow } from './rows/log-entry/LogEntryRow';
import { LogEntryRowModel } from './rows/log-entry/LogEntryRowModel';
import { LogViewerTableSummaryRow } from './rows/summary/SummaryRow';
import { SummaryRowModel } from './rows/summary/SummaryRowModel';

interface Props {
  log: Log;
  delegate: LogViewerTableDelegate;
  postProcessing: LogViewerPostProcessing;
  postProcessingDelegate: PostProcessingDelegate;
  settings: AppSettings;
  networkOutcomeFilter: NetworkOutcomeFilter;
}

export interface PostProcessingDelegate {
  postProcessingOperationStarted(): void;
  postProcessingOperationSucceeded(foundOccurances: FoundOccurancesPerHighlighter[]): void;
  postProcessingOperationFailed(error: any): void;
}

export interface LogViewerTableDelegate {
  onBookmarkedRowModelsChange(rowModels: LogViewerTableRowModel[]): void;
  onNoBugButtonFound?(): void;
}

type State = {
  timelineHeight: number;
  isChangingTimelineHeight: boolean;

  mapWidth: number;
  hoveredLogEntry?: {
    logEntryIndex: number;
    timeMs: number;
  };
};

export class LogViewerTable
  extends React.Component<Props, State>
  implements LogViewerTableModelDelegate, TimelineDelegate
{
  private cellMeasurerCache: CellMeasurerCache;
  private listRef = React.createRef<List>();
  private map = React.createRef<Map>();

  // TODO: move !: variables to State.
  private _model!: LogViewerTableModel;
  private lastScrollTop: number = 0;

  /**
   * Indices into the model's own visible-row list, after applying the
   * network outcome filter on top. Rebuilt whenever the model changes or
   * the filter prop changes.
   */
  private _filteredDisplayIndices: number[] = [];

  private timelineData!: TimelineData;
  private height?: number;

  get model(): LogViewerTableModel {
    return this._model;
  }

  constructor(props: Props) {
    super(props);

    this.cellMeasurerCache = new CellMeasurerCache({
      fixedWidth: true,
      minHeight: 20,
    });
    this.state = {
      timelineHeight: this.loadTimelineHeight() || 300,
      isChangingTimelineHeight: false,
      mapWidth: 500,
      hoveredLogEntry: undefined,
    };
  }

  private subscribeForWindowEvents() {
    window.addEventListener('resize', this.handleWindowResize);
    window.addEventListener('keydown', this.handleWindowKeydown);

    window.addEventListener('mousemove', this.handleWindowMouseMove);
    window.addEventListener('mouseup', this.handleWindowMouseUp);
  }

  private unsubscribeFromWindowEvents() {
    window.removeEventListener('mousemove', this.handleWindowMouseMove);
    window.removeEventListener('mouseup', this.handleWindowMouseUp);

    window.removeEventListener('keydown', this.handleWindowKeydown);
    window.removeEventListener('resize', this.handleWindowResize);
  }

  private handleWindowResize = () => {
    // Window gets resized, cells size is no more valid.
    this.cellMeasurerCache.clearAll();
  };

  private handleWindowMouseUp = () => {
    if (this.state.isChangingTimelineHeight) {
      this.setState({ isChangingTimelineHeight: false });
    }
  };

  private handleWindowMouseMove = (event: MouseEvent) => {
    if (this.state.isChangingTimelineHeight && this.height) {
      const height = this.height;
      const minTimelineHeight = 300;
      const maxTimelineHeight = height > 300 ? height - 300 : height;

      this.setState((prevState) => {
        let newTimelineHeight = prevState.timelineHeight - event.movementY;
        newTimelineHeight = _.clamp(newTimelineHeight, minTimelineHeight, maxTimelineHeight);
        this.saveTimelineHeight(newTimelineHeight);
        this.setState({
          timelineHeight: newTimelineHeight,
        });
      });
      event.preventDefault();
    }
  };

  private saveTimelineHeight(h: number) {
    localStorage.setItem('timelineHeight', h.toString());
  }

  private loadTimelineHeight(): number | undefined {
    const hStr = localStorage.getItem('timelineHeight');
    return hStr ? parseInt(hStr) : undefined;
  }

  private handleWindowKeydown = (event: KeyboardEvent) => {
    if (event.metaKey) {
      const displayedRowsCount = this._model.getRowsCountToDisplay();
      if (displayedRowsCount > 0) {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.scrollToRow(this._model.getRowsCountToDisplay() - 1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          this.scrollToRow(0);
        }
      }

      if (event.key === 'l') {
        event.preventDefault();

        const lineNumberStr = prompt('Enter row index to jump to:');
        if (typeof lineNumberStr === 'string') {
          const lineNumber = parseInt(lineNumberStr);
          this.scrollToRowIndexBeforeFiltering(lineNumber);
        }
      }
    }
  };

  private scrollToLogEntryIndex(logEntryIndex: number) {
    const rowIndexBeforeFiltering =
      this._model.convertLogEntryIndexToRowIndexBeforeFiltering(logEntryIndex);
    if (typeof rowIndexBeforeFiltering === 'undefined') {
      console.error('Invalid row');
      return;
    }

    this.scrollToRowIndexBeforeFiltering(rowIndexBeforeFiltering);
  }

  private scrollToRowIndexBeforeFiltering(rowIndexBeforeFiltering: number) {
    if (rowIndexBeforeFiltering < 0 || rowIndexBeforeFiltering >= this.model.allRowModels.length) {
      return;
    }

    this._model.selectedRowIndexBeforeFiltering = rowIndexBeforeFiltering;

    const rowIndexAfterFiltering =
      this._model.allRowModels[rowIndexBeforeFiltering].data.indexAfterApplyingFilters;
    if (typeof rowIndexAfterFiltering === 'undefined') {
      console.log('Row is not shown, cannot scroll to it');
      return;
    }

    this.scrollToRow(rowIndexAfterFiltering);
  }

  componentDidMount() {
    this._model = makeLogViewerTableModel({
      log: this.props.log,
      postProcessing: this.props.postProcessing,
      settings: this.props.settings,
      delegate: this,
    });
    this.timelineData = TimelineDataGenerator.generateFromLog(
      this.props.log,
      this.convertLogEntryIndexToLineIndex,
    );

    this.syncContextualEventsVisibility();
    this.subscribeForWindowEvents();
    this.forceUpdate();

    console.log('Table model', this._model);
  }

  componentWillUnmount() {
    this.unsubscribeFromWindowEvents();
    this._model.componentWillUnmount();
  }

  componentDidUpdate(prevProps: Props) {
    this._model.reset({
      log: this.props.log,
      postProcessing: this.props.postProcessing,
      settings: this.props.settings,
    });

    if (this.timelineData.logId !== this.props.log.uniqueId) {
      this.timelineData = TimelineDataGenerator.generateFromLog(
        this.props.log,
        this.convertLogEntryIndexToLineIndex,
      );
    }

    // Re-sync whenever log or filter changes
    if (
      prevProps.log !== this.props.log ||
      prevProps.networkOutcomeFilter !== this.props.networkOutcomeFilter
    ) {
      this.syncContextualEventsVisibility();
    }

    // When the network filter changes, the row count changes —
    // tell react-virtualized to re-render with the new count.
    if (prevProps.networkOutcomeFilter !== this.props.networkOutcomeFilter) {
      this.cellMeasurerCache.clearAll();
      this.listRef.current?.forceUpdateGrid();
    }
  }

  private scrollToRow(index: number): Promise<void> {
    const list = this.listRef.current;
    if (!list) {
      return new Promise((fulfill) => fulfill());
    }

    // Iteration is needed because cell height is dynamic, and scroll may shift a bit while
    // calculating the closest rows heights.
    return new Promise((fulfill) => {
      const maxAttempts = 10;
      for (let i = 0; i < maxAttempts; ++i) {
        const offset = list.getOffsetForRow({ index, alignment: 'start' });
        list.scrollToPosition(offset);

        const offset2 = list.getOffsetForRow({ index, alignment: 'start' });

        if (Math.abs(offset2 - offset) < 1.0) {
          fulfill();
          break;
        }
      }
    });
  }

  jumpToSearchTerm(searchId: string, termIndex: number) {
    const occurance = this._model.getFoundOccurance(searchId, termIndex);
    if (!occurance || occurance.htmlIds.length === 0) {
      return;
    }

    const row = this._model.allRowModels[occurance.rowIndexBeforeFiltering];
    if (typeof row.data.indexAfterApplyingFilters === 'undefined') {
      return;
    }

    if (row.data.indexBeforeApplyingFilters !== this._model.selectedRowIndexBeforeFiltering) {
      this._model.selectedRowIndexBeforeFiltering = row.data.indexBeforeApplyingFilters;
    }

    ActiveHighlightableString.setActivePartIds(occurance.htmlIds);

    let shouldScrollToRowFirst: boolean;

    const firstOccuranceHtml = document.getElementById(occurance.htmlIds[0]);
    if (firstOccuranceHtml === null) {
      shouldScrollToRowFirst = true;
    } else {
      const rect = firstOccuranceHtml.getClientRects()[0];
      const isVisibleOnScreen = rect.top > 0 && rect.top < window.innerHeight;
      shouldScrollToRowFirst = !isVisibleOnScreen;
    }

    if (shouldScrollToRowFirst) {
      this.scrollToRow(row.data.indexAfterApplyingFilters);

      setTimeout(() => {
        const firstOccuranceHtml = document.getElementById(occurance.htmlIds[0]);
        firstOccuranceHtml?.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'nearest',
        });
      }, 10);
    } else {
      const firstOccuranceHtml = document.getElementById(occurance.htmlIds[0]);
      firstOccuranceHtml?.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }

  get shouldShowHeader(): boolean {
    return this._model.additionalColumnDefinitions.length > 0;
  }

  get headerHeight(): number {
    return 64;
  }

  get mapWidth(): number {
    return this.state.mapWidth;
  }

  render() {
    if (this._model === undefined) {
      return null;
    }

    const timelineHeight =
      this.props.settings.enableTimelineV2 === true ? this.state.timelineHeight : 0;
    const timelineResizerHeight = 12;

    let rowIndexAtTop = 0;
    let rowOffsetAtTop = 0;

    return (
      <AutoSizer>
        {(a: { width: number; height: number }) => {
          const listHeight =
            (this.shouldShowHeader ? a.height - this.headerHeight : a.height) - timelineHeight;
          this.height = a.height;

          return (
            <div>
              {this.shouldShowHeader ? (
                <div style={{ width: a.width }} className="log-viewer-table-header-container">
                  <LogViewerTableHeader
                    additionalColumnDefinitions={this._model.additionalColumnDefinitions}
                  />
                </div>
              ) : null}
              <div style={{ width: a.width, height: listHeight, position: 'relative' }}>
                <List
                  style={{ position: 'absolute' }}
                  ref={this.listRef}
                  width={a.width - (this.props.settings.enableMap ? this.state.mapWidth : 0)}
                  height={listHeight}
                  overscanRowCount={0}
                  deferredMeasurementCache={this.cellMeasurerCache}
                  rowHeight={this.cellMeasurerCache.rowHeight}
                  rowCount={this.rowsCount()}
                  rowRenderer={(a) => {
                    return this.rowRenderer(a);
                  }}
                  onScroll={(a) => this.onScroll(a)}
                  onRowsRendered={(a) => {
                    const offset = this.listRef.current?.getOffsetForRow({
                      alignment: 'start',
                      index: a.startIndex,
                    })!;

                    const isScrollingDown = a.startIndex === rowIndexAtTop - 1;
                    if (isScrollingDown) {
                      const prevRowAtTopOffset = this.listRef.current?.getOffsetForRow({
                        alignment: 'start',
                        index: rowIndexAtTop,
                      })!;

                      const diff = rowOffsetAtTop - prevRowAtTopOffset;
                      if (diff < 0) {
                        // Fix react-virtualized UI glitch which happens when:
                        // - user scrolls to row somewhere in the middle (say, taps on a marker on timeline)
                        // - user starts scrolling upward
                        // - when new row at the appears, react-virtualized measures its height
                        // - height is typically bigger the estimated
                        // - with that, react-virtualized keeps the same scrollOffset and draws new row at the top
                        // - visually, it looks like the content has been scrolled to the bottom a bit

                        const prevRowOffsetFromTop = rowOffsetAtTop - this.lastScrollTop;
                        this.listRef.current?.scrollToPosition(
                          prevRowAtTopOffset - prevRowOffsetFromTop,
                        );
                      }
                    }

                    rowIndexAtTop = a.startIndex;
                    rowOffsetAtTop = offset;
                  }}
                />
                {this.props.settings.enableMap ? (
                  <Map
                    ref={this.map}
                    width={this.state.mapWidth}
                    height={listHeight}
                    style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                    }}
                    log={this.props.log}
                  />
                ) : null}
              </div>
              {this.props.settings.enableTimelineV2 === true ? (
                <div style={{ width: a.width, height: timelineHeight, overflow: 'auto' }}>
                  <div
                    className="timeline-resizer"
                    style={{ height: timelineResizerHeight, width: a.width }}
                    onMouseDown={(event) => {
                      if (event.buttons === 1) {
                        this.setState({ isChangingTimelineHeight: true });
                      }
                    }}
                  />
                  <Timeline
                    data={this.timelineData}
                    delegate={this}
                    hoveredLogEntry={this.state.hoveredLogEntry}
                  />
                </div>
              ) : null}
            </div>
          );
        }}
      </AutoSizer>
    );
  }

  // ---------------------------------------------------------------------------
  // Network outcome filtering — applied on top of the model's own visible rows
  // ---------------------------------------------------------------------------

  /**
   * "Contextual events" are the log entries immediately surrounding a network
   * request/response — the chips on a network row expand inline to show the
   * entries before or after it, paging in more on demand.
   *
   * They exist because the network state filter hides every non-network row: you
   * can see that a call failed, but not what the app was doing around it. The
   * chips give you that context without clearing the filter and hunting for the
   * row again.
   *
   * They're therefore only shown while the filter is active — with no filter the
   * surrounding entries are already visible in the table, so the chips would be
   * redundant. This keeps the showContextualEvents flag on each network cell in
   * sync with the filter state.
   */
  private syncContextualEventsVisibility() {
    if (!this._model) {
      return;
    }
    const filterActive = isNetworkOutcomeFilterActive(this.props.networkOutcomeFilter);
    const total = this._model.getRowsCountToDisplay();
    for (let i = 0; i < total; i++) {
      const rowModel = this._model.getRowModelToDisplayAt(i);
      if (rowModel.data.type === LogViewerTableRowType.LogEntry) {
        const entryRow = rowModel as LogEntryRowModel;
        const allCells = [entryRow.mainCell, ...(entryRow.additionalCells ?? [])];
        for (const cell of allCells) {
          const networkCell = cell as unknown as LogEntryNetworkCellModel;
          if (networkCell && typeof networkCell.showContextualEvents === 'boolean') {
            networkCell.showContextualEvents = filterActive;
          }
        }
      }
    }
  }

  /**
   * Build a list of display-indices (indices into the model's visible row list)
   * that pass the current network outcome filter. When the filter is inactive,
   * this is simply [0, 1, 2, ..., n-1].
   */
  private buildFilteredDisplayIndices(): number[] {
    const total = this._model.getRowsCountToDisplay();
    const filter = this.props.networkOutcomeFilter;

    if (!isNetworkOutcomeFilterActive(filter)) {
      return Array.from({ length: total }, (_, i) => i);
    }

    const result: number[] = [];
    for (let i = 0; i < total; i++) {
      if (this.rowPassesNetworkFilter(i, filter)) {
        result.push(i);
      }
    }
    return result;
  }

  /**
   * Check whether the row at display-index `displayIdx` passes the network
   * outcome filter. Reads directly from the parsed LogEntry — no worker needed.
   */
  private rowPassesNetworkFilter(displayIdx: number, filter: NetworkOutcomeFilter): boolean {
    const rowModel = this._model.getRowModelToDisplayAt(displayIdx);

    // Hide summary rows, file-divider rows, and non-network rows when filter is active —
    // the user wants to see ONLY the matching network rows.
    if (rowModel.data.type !== LogViewerTableRowType.LogEntry) {
      return false;
    }

    const logEntry = (rowModel as LogEntryRowModel).logEntry;

    let isRequest: boolean;
    if (logEntry.entryType === LogEntryType.NetworkRequest) {
      isRequest = true;
    } else if (logEntry.entryType === LogEntryType.NetworkResponse) {
      isRequest = false;
    } else {
      // Generic / Redux / MQTT rows — hide them when network filter is active
      return false;
    }

    // Check whether this side is included in the filter
    const { side } = filter;
    if (side === NetworkOutcomeFilterSide.Requests && !isRequest) {
      return false;
    }
    if (side === NetworkOutcomeFilterSide.Responses && isRequest) {
      return false;
    }

    // Check outcome directly from the parsed log entry
    const outcome: NetworkRequestOutcome =
      logEntry.extra.networkRequest?.outcome ?? NetworkRequestOutcome.Unknown;

    return filter.outcomes.includes(outcome);
  }

  // ---------------------------------------------------------------------------

  private rowsCount(): number {
    // Always recompute here so the index array is guaranteed fresh
    // for the rowRenderer calls that follow in the same render cycle.
    this._filteredDisplayIndices = this.buildFilteredDisplayIndices();
    return this._filteredDisplayIndices.length;
  }

  private rowRenderer(a: { index: number; parent: any; key: string; style: any }) {
    // Map filtered index → model display index
    const modelDisplayIndex = this._filteredDisplayIndices[a.index];
    const rowModel = this._model.getRowModelToDisplayAt(modelDisplayIndex);
    let rowElement: ReactElement;

    switch (rowModel.data.type) {
      case LogViewerTableRowType.LogEntry:
        const entryRowModel = rowModel as LogEntryRowModel;
        rowElement = (
          <LogEntryRow
            model={entryRowModel}
            onClick={() => {
              this.onRowClick(rowModel);
            }}
            onHoverChange={(hovered) => {
              this.onLogEntryRowHoverChange(entryRowModel, hovered);
            }}
          />
        );
        break;
      case LogViewerTableRowType.Summary:
        const summaryRowModel = rowModel as SummaryRowModel;
        rowElement = (
          <LogViewerTableSummaryRow
            model={summaryRowModel}
            onClick={() => {
              this.onRowClick(rowModel);
            }}
          />
        );
        break;
      case LogViewerTableRowType.FileDivider:
        const fileDividerRowModel = rowModel as FileDividerRowModel;
        rowElement = <FileDividerRow model={fileDividerRowModel} />;
        break;
    }

    return (
      <CellMeasurer cache={this.cellMeasurerCache} key={a.key} rowIndex={a.index} parent={a.parent}>
        {(props) => {
          return (
            <div
              // This ref supresses StrictMode warning: https://github.com/bvaughn/react-virtualized/issues/1572
              ref={props.registerChild as React.LegacyRef<HTMLDivElement>}
              style={{ ...a.style }}
            >
              {rowElement}
            </div>
          );
        }}
      </CellMeasurer>
    );
  }

  private onRowClick(rowModel: LogViewerTableRowModel) {
    const rowIndex = rowModel.data.indexBeforeApplyingFilters;
    if (this._model.selectedRowIndexBeforeFiltering === rowIndex) {
      this._model.selectedRowIndexBeforeFiltering = undefined;
    } else {
      this._model.selectedRowIndexBeforeFiltering = rowIndex;
    }
  }

  private onLogEntryRowHoverChange(rowModel: LogEntryRowModel, hovered: boolean) {
    const hoveredLogEntry = {
      logEntryIndex: rowModel.logEntry.index,
      timeMs: rowModel.logEntry.date.getTime(),
    };

    if (hovered) {
      this.setState({ hoveredLogEntry });
      return;
    }

    this.setState((prevState) => {
      if (prevState.hoveredLogEntry?.logEntryIndex !== hoveredLogEntry.logEntryIndex) {
        return null;
      }

      return { hoveredLogEntry: undefined };
    });
  }

  // MARK: - LogViewerTableModelDelegate

  onModelChange(context: ModelChangeContext): void {
    console.time('onModelChange');

    if (context.shouldClearRowHeightCache) {
      this.cellMeasurerCache.clearAll();
    } else if (typeof context.rowIndicesBeforeFilteringToRefresh !== 'undefined') {
      context.rowIndicesBeforeFilteringToRefresh.forEach((indexBeforeFiltering) => {
        const indexAfterFiltering =
          this._model.allRowModels[indexBeforeFiltering].data.indexAfterApplyingFilters;
        if (typeof indexAfterFiltering !== 'undefined') {
          console.log('Invalidate row', indexBeforeFiltering, indexAfterFiltering);
          this.cellMeasurerCache.clear(indexAfterFiltering, 0);
          this.listRef.current?.forceUpdateGrid();
        }
      });
    }

    console.timeLog('onModelChange', 'Before forceUpdate');
    this.forceUpdate();
    console.timeLog('onModelChange', 'After forceUpdate');

    console.timeEnd('onModelChange');
  }

  onBookmarkedRowsChange(models: LogViewerTableRowModel[]): void {
    this.props.delegate.onBookmarkedRowModelsChange(models);
  }

  private onScroll(params: ScrollParams) {
    this.lastScrollTop = params.scrollTop;
  }

  onRequestScrollToRowWithIndexAfterFiltering(rowIndex: number): void {
    this.scrollToRow(rowIndex);
  }

  postProcessingOperationStarted(): void {
    this.props.postProcessingDelegate.postProcessingOperationStarted();
  }

  private offsetToRowStartFromListTop: number | undefined;

  postProcessingOperationIsAboutToComplete(): void {
    if (
      typeof this._model.selectedRowIndexBeforeFiltering !== 'undefined' &&
      this.listRef.current
    ) {
      const index =
        this._model.allRowModels[this._model.selectedRowIndexBeforeFiltering].data
          .indexAfterApplyingFilters;
      const offsetToRowStart = this.listRef.current.getOffsetForRow({ index, alignment: 'start' });
      this.offsetToRowStartFromListTop = offsetToRowStart - this.lastScrollTop;
    }
  }

  postProcessingOperationCompleted(
    occurancesPerHighlighter: FoundOccurancesPerHighlighter[],
  ): void {
    console.time('ppEnd');
    if (
      typeof this.offsetToRowStartFromListTop !== 'undefined' &&
      typeof this._model.selectedRowIndexBeforeFiltering !== 'undefined' &&
      this.listRef.current
    ) {
      const index =
        this._model.allRowModels[this._model.selectedRowIndexBeforeFiltering].data
          .indexAfterApplyingFilters;
      if (typeof index !== 'undefined') {
        console.timeLog('ppEnd', 'Before scrolling');

        const offsetToRowStart = this.listRef.current.getOffsetForRow({
          index,
          alignment: 'start',
        });
        let neededScrollOffset = Math.max(0, offsetToRowStart - this.offsetToRowStartFromListTop);

        console.timeLog('ppEnd', '1');

        const maxScrollsIterations = 10;
        for (let scrollIteration = 0; scrollIteration < maxScrollsIterations; ++scrollIteration) {
          console.timeLog('ppEnd', `iteration ${scrollIteration} start`);

          this.listRef.current?.scrollToPosition(offsetToRowStart);

          console.timeLog('ppEnd', '2');

          let updatedOffsetToRowStart = this.listRef.current.getOffsetForRow({
            index,
            alignment: 'start',
          });
          let updatedNeededScrollOffset = Math.max(
            0,
            updatedOffsetToRowStart - this.offsetToRowStartFromListTop,
          );

          console.timeLog('ppEnd', '3');
          this.listRef.current?.scrollToPosition(updatedNeededScrollOffset);
          console.timeLog('ppEnd', '4');

          updatedOffsetToRowStart = this.listRef.current.getOffsetForRow({
            index,
            alignment: 'start',
          });
          updatedNeededScrollOffset = Math.max(
            0,
            updatedOffsetToRowStart - this.offsetToRowStartFromListTop,
          );
          console.timeLog('ppEnd', '5');

          if (Math.abs(updatedNeededScrollOffset - neededScrollOffset) < 1.0) {
            this.listRef.current?.scrollToPosition(updatedNeededScrollOffset);
            break;
          }
          console.timeLog('ppEnd', '6');
          neededScrollOffset = updatedNeededScrollOffset;

          console.timeLog('ppEnd', `iteration ${scrollIteration} end`);
        }

        console.timeLog('ppEnd', 'After scrolling');
      }
    }

    console.timeLog('ppEnd', 'About to complete');
    ActiveHighlightableString.clearActivePartIds();
    this.props.postProcessingDelegate.postProcessingOperationSucceeded(occurancesPerHighlighter);
    console.timeEnd('ppEnd');
  }

  postProcessingOperationFailed(error: any): void {
    this.props.postProcessingDelegate.postProcessingOperationFailed(error);
  }

  onNoBugButtonFound(): void {
    this.props.delegate.onNoBugButtonFound?.();
  }

  // MARK: - TimelineDelegate

  onRequestJumpToLogEntry(logEntryIndex: number): void {
    this.scrollToLogEntryIndex(logEntryIndex);
  }

  onRequestSelectGpsHistoryLocation(
    gpsHistoryLocationIndex: number,
    color: string | undefined,
  ): void {
    const location = this.props.log.gpsHistory.locations[gpsHistoryLocationIndex];

    this.scrollToLogEntryIndex(location.logEntryIndex);

    if (this.map.current) {
      this.map.current.selectLocation({ location, color });
    }
  }

  onRequestSelectMapEvent(logEntryIndex: number, mapEvent: MapEventInfo): void {
    this.scrollToLogEntryIndex(logEntryIndex);

    if (this.map.current) {
      this.map.current.setMapContext(mapEvent);
    }
  }

  convertLogEntryIndexToLineIndex = (logEntryIndex: number) => {
    return this._model.convertLogEntryIndexToRowIndexBeforeFiltering(logEntryIndex);
  };
}
